import type { DevicePorts } from "./ports";
import type { Player } from "../utils/player/createPlayer";
import type { DownloadManager } from "../utils/downloadManager";

async function clearAccountStorage(storage: DevicePorts["asyncStorage"]) {
  const keys = await storage.getAllKeys();
  // Firebase may have already persisted the next account. Its SDK owns both
  // these keys and their removal on sign-out.
  await storage.multiRemove(keys.filter((key) => !key.startsWith("firebase:")));
}

export async function resetAccount(
  ports: DevicePorts,
  downloads: DownloadManager,
  player: Player,
) {
  const cleanup = ports.accountCleanup;
  const cancelledDownloads = await Promise.allSettled([downloads.reset()]);
  const outcomes = await Promise.allSettled([
    cleanup.clearDiskCache(),
    cleanup.clearMemoryCache(),
    Promise.resolve().then(() => player.reset()),
    ports.platform.os === "web"
      ? Promise.resolve()
      : clearAccountStorage(ports.asyncStorage),
    cleanup.logoutSupport(),
    cleanup.resetAnalytics(),
    cleanup.resetFlip(),
  ]);
  if (ports.platform.os === "web") {
    try {
      cleanup.clearVendorAttempts();
    } catch {
      // Private browsing can disable storage; account reset still proceeds.
    }
  }
  cleanup.clearTelemetryUser();
  const errors = [...cancelledDownloads, ...outcomes].flatMap((outcome) =>
    outcome.status === "rejected" ? [outcome.reason] : [],
  );
  if (errors.length) throw new AggregateError(errors, "Account reset failed");
}
